import { useState } from 'react';
import { Box, Fab, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Stack, useMediaQuery, useTheme } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart, X, Send } from 'lucide-react';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { submitQuickOrder } from '../../lib/api.js';
import GlowButton from './GlowButton';

const orderSchema = z.object({
  name: z.string().min(2, 'Name is required'),
  email: z.string().email('Invalid email'),
  phone: z.string().min(10, 'Phone is required'),
  service: z.string().min(2, 'Service is required'),
  message: z.string().optional(),
});

const QuickOrderButton = () => {
  const [open, setOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({
    resolver: zodResolver(orderSchema),
  });

  const onSubmit = async (data) => {
    try {
      const result = await submitQuickOrder(data);
      enqueueSnackbar(result.message, {
        variant: 'success',
      });
      reset();
      setOpen(false);
    } catch (error) {
      enqueueSnackbar(error.message || 'Failed to submit order. Please try again.', { variant: 'error' });
    }
    return false; // Prevent any default form behavior
  };

  return (
    <>
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 200, damping: 15 }}
        style={{
          position: 'fixed',
          bottom: isMobile ? 16 : 24,
          right: isMobile ? 16 : 24,
          zIndex: 1000,
        }}
      >
        <Tooltip title="Quick Order / Get Quote" placement="left">
          <Fab
            onClick={() => setOpen(true)}
            sx={{
              background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
              color: 'white',
              width: isMobile ? 56 : 64,
              height: isMobile ? 56 : 64,
              boxShadow: '0 10px 40px rgba(168, 85, 247, 0.5)',
              '&:hover': {
                background: 'linear-gradient(135deg, #9333ea 0%, #db2777 100%)',
                boxShadow: '0 15px 50px rgba(168, 85, 247, 0.7)',
                transform: 'scale(1.1)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
            >
              <ShoppingCart size={isMobile ? 24 : 28} />
            </motion.div>
          </Fab>
        </Tooltip>
      </motion.div>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(11, 15, 26, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.18)',
            borderRadius: '24px',
          },
        }}
      >
        <DialogTitle sx={{ color: 'white', fontWeight: 700, fontSize: '1.5rem' }}>
          Quick Order / Get Quote
        </DialogTitle>
        <form 
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSubmit(onSubmit)(e);
          }} 
          noValidate
        >
          <DialogContent>
            <Stack spacing={3}>
              <TextField
                label="Full Name *"
                {...register('name')}
                error={!!errors.name}
                helperText={errors.name?.message}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: 'rgba(255, 255, 255, 0.05)',
                    color: 'white',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&:hover fieldset': {
                      borderColor: '#a855f7',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#a855f7',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#a855f7',
                  },
                }}
              />
              <TextField
                label="Email *"
                type="email"
                {...register('email')}
                error={!!errors.email}
                helperText={errors.email?.message}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: 'rgba(255, 255, 255, 0.05)',
                    color: 'white',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&:hover fieldset': {
                      borderColor: '#a855f7',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#a855f7',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#a855f7',
                  },
                }}
              />
              <TextField
                label="Phone *"
                {...register('phone')}
                error={!!errors.phone}
                helperText={errors.phone?.message}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: 'rgba(255, 255, 255, 0.05)',
                    color: 'white',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&:hover fieldset': {
                      borderColor: '#a855f7',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#a855f7',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#a855f7',
                  },
                }}
              />
              <TextField
                label="Service Needed *"
                {...register('service')}
                error={!!errors.service}
                helperText={errors.service?.message}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: 'rgba(255, 255, 255, 0.05)',
                    color: 'white',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&:hover fieldset': {
                      borderColor: '#a855f7',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#a855f7',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#a855f7',
                  },
                }}
              />
              <TextField
                label="Message (Optional)"
                multiline
                rows={4}
                {...register('message')}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: 'rgba(255, 255, 255, 0.05)',
                    color: 'white',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&:hover fieldset': {
                      borderColor: '#a855f7',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#a855f7',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#a855f7',
                  },
                }}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 2 }}>
            <Button
              onClick={() => setOpen(false)}
              startIcon={<X />}
              sx={{ color: 'text.secondary' }}
            >
              Cancel
            </Button>
            <GlowButton
              type="submit"
              disabled={isSubmitting}
              endIcon={<Send size={18} />}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Order'}
            </GlowButton>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

export default QuickOrderButton;
